import React, {useState} from 'react';
import {Col, Dropdown, Row} from 'react-bootstrap';
import {Button} from '@/components/ui';
import {Tooltip} from '@/components/tooltips';
import {Icon} from '@/components/icons/Icon';
import PaginationComponent from '@/components/pagination/PaginationComponent';

import AwesomeDebouncePromise from 'awesome-debounce-promise';
import Form from 'react-bootstrap/Form';
import axios from 'axios';
import {useStoreActions} from 'easy-peasy';
import usePaginatedData from '@/utils/usePaginatedData';
import LoadingWrapper from '@/components/loading/LoadingWrapper';
import TranscriptionTasksSettingsModal from './TranscriptionTasksSettingsModal';
import HelpTextBlock from '@/components/help_text/HelpTextBlock';
import useOptimizedConfig from '@/hooks/useOptimizedConfig';
import {buildEventDetailURL} from '@/utils/urlBuilder';
import TableComponent from '@/components/table/Table';
import {navigate} from '@reach/router';
import i18n from 'i18n';
import {useFormattedDateTime} from '@/components/utils/FormattedDateTime';
import {ModalAlert} from '@/components/alerts';
import {hasPermission} from '@/utils/permissions/permissionDebug';

interface TranscriptionTask {
    id: number;
    createdOn: string;
    eventLang: string | null;
    eventDatetime: string;
    externalTaskId: string;
    status: string | null;
    eventId: number;
}

interface UserData {
    permissions?: string[];
}

interface TranscriptionTasksStore {
    deleteOne: (id: number) => void;
}

const setSearchQueryState = async (
    setSearchQuery: (value: string) => void,
    value: string,
    setPage: (page: number) => void
): Promise<void> => {
    setPage(1);
    setSearchQuery(value);
};

const setSearchQueryStateDebounced = AwesomeDebouncePromise(setSearchQueryState, 350);

const TranscriptionTasksListView: React.FC = React.memo(() => {
    const {formatDateTime, isLoaded: configLoaded} = useFormattedDateTime();
    const {userData, loading: loadingUserData} = useOptimizedConfig();

    const [settingsModalOpen, setSettingsModalOpen] = useState<boolean>(false);
    const [selectedTranscriptionTaskId, setSelectedTranscriptionTaskId] = useState<number>(0);

    const [searchQuery, setSearchQuery] = useState<string>('');
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState<boolean>(false);
    const [taskToDelete, setTaskToDelete] = useState<{id: number, externalTaskId: string} | null>(null);
    const [isDeleting, setIsDeleting] = useState<boolean>(false);

    const {
        data: transcriptionTasks,
        loading: loadingTranscriptionTasks,
        currentPage,
        setCurrentPage,
        pages,
    } = usePaginatedData(window.URLS.transcriptionTasksResource, {
        storeResolver: 'transcriptionTasks',
        requestParams: {
            params: {
                query: searchQuery,
            },
        },
    });

    const deleteTranscriptionTask = useStoreActions((actions: {transcriptionTasks: TranscriptionTasksStore}) => actions.transcriptionTasks.deleteOne);

    const canDeleteTranscriptionTask = hasPermission('fs.transcription_task.delete', userData?.permissions, true);
    const canAccessCallDetail = hasPermission('fs.event.read', userData?.permissions, true);

    const userCanAccessTranscriptionTasksResource = hasPermission('fs.transcription_task.list', userData?.permissions, true);

    if (!userCanAccessTranscriptionTasksResource && !loadingUserData) {
        return (
            <>
                <HelpTextBlock helpText={i18n.t('ht_the_transcription_tasks.text')} />

                <div className="alert alert-danger" role="alert">
                    {i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
                </div>
            </>
        );
    }

    const inputChange = (event: React.ChangeEvent<any>): void => {
        setSearchQueryStateDebounced(setSearchQuery, event.target.value, setCurrentPage);
    };

    const openSettingsModal = (transcriptionTaskId: number): void => {
        setSelectedTranscriptionTaskId(transcriptionTaskId);
        setSettingsModalOpen(true);
    };

    const closeSettingsModal = (): void => {
        setSettingsModalOpen(false);
        setSelectedTranscriptionTaskId(0);
    };

    const active = currentPage;
    let items: React.ReactNode;

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    const openDeleteConfirm = (id: number, externalTaskId: string): void => {
        setTaskToDelete({id, externalTaskId});
        setDeleteConfirmOpen(true);
    };

    const closeDeleteConfirm = (): void => {
        setTaskToDelete(null);
        setDeleteConfirmOpen(false);
        setIsDeleting(false);
    };

    const confirmDelete = async (): Promise<void> => {
        if (!taskToDelete) {return;}

        setIsDeleting(true);
        try {
            const url = `${window.URLS.transcriptionTasksResource}/${taskToDelete.id}`;
            await axios.delete(url);
            deleteTranscriptionTask(taskToDelete.id);
            closeDeleteConfirm();
        } catch (error) {
            setIsDeleting(false);
            throw error;
        }
    };

    const deleteOne = (id: number, externalTaskId: string): void => {
        openDeleteConfirm(id, externalTaskId);
    };

    const detailUrlGenerator = (rowData: TranscriptionTask): string => {
        return buildEventDetailURL(rowData.eventId);
    };

    const columns = [
        {
            accessorKey: 'createdOn',
            header: i18n.t('th_task_created.text'),
            cell: (cell: {row: {original: TranscriptionTask}}) => {
                return configLoaded ? formatDateTime(cell.row.original.createdOn, true, {setZone: false},
                ) : '';
            },
        },
        {
            accessorKey: 'eventLang',
            header: i18n.t('th_lang.text'),
            cell: (cell: {getValue: () => string | null}) => {
                if (cell.getValue() === null) {
                    return '-';
                } else {
                    return cell.getValue();
                }
            },
        },
        {
            accessorKey: 'eventDatetime',
            header: i18n.t('th_call_datetime.text'),
        },
        {
            accessorKey: 'externalTaskId',
            header: i18n.t('th_external_task.text'),
        },
        {
            accessorKey: 'status',
            header: i18n.t('th_status.text'),
            cell: (cell: {getValue: () => string | null}) => {
                if (cell.getValue() === null) {
                    return '-';
                } else {
                    return cell.getValue();
                }
            },
        },
        {
            header: i18n.t('th_action.text'),
            cell: (cell: {row: {original: TranscriptionTask}}) => (
                <div className="d-flex align-items-center justify-content-end w-100">
                    <div className="d-inline-block mr-2">
                        {
                            canAccessCallDetail ? (
                                <Tooltip content={i18n.t('jt_view.text')} placement="top">
                                    <Button
                                        variant="outline-info"
                                        size="sm"
                                        icon="mdi-eye"
                                        onClick={() => navigate(detailUrlGenerator(cell.row.original))}
                                    />
                                </Tooltip>
                            ) : (
                                <Tooltip
                                    content={i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
                                    placement="top"
                                >
                                    <div style={{
                                        display: 'inline-block',
                                        cursor: 'not-allowed',
                                    }}
                                    >
                                        <Button
                                            variant="outline-secondary"
                                            size="sm"
                                            icon="mdi-eye"
                                            disabled
                                            style={{pointerEvents: 'none'}}
                                            data-cy="transcription-tasks-list-eye-outlinesecondary-btn" />
                                    </div>
                                </Tooltip>
                            )
                        }
                    </div>
                    <Tooltip content={i18n.t('jt_actions.text')} placement="top">
                        <Dropdown className="d-inline-block ml-2">
                            <Dropdown.Toggle
                                as={Button}
                                variant="outline-info"
                                size="sm"
                                icon="mdi-dots-horizontal"
                                className="dropdown-toggle-no-caret"
                                id="settings-dropdown-1"
                                data-cy="transcription-tasks-list-outlineinfo-btn"
                            />

                            <Dropdown.Menu align="end">
                                <Dropdown.Item
                                    onClick={() => openSettingsModal(cell.row.original.id)}
                                >
                                    <Icon name="mdi-settings" className="mr-2" /> {i18n.t('jt_settings.text')}
                                </Dropdown.Item>
                                {
                                    canDeleteTranscriptionTask ? (
                                        <Dropdown.Item
                                            onClick={() => deleteOne(cell.row.original.id, cell.row.original.externalTaskId)}
                                        >
                                            <Icon name="mdi-delete-outline" className="mr-2" /> {i18n.t('jt_delete.text')}
                                        </Dropdown.Item>
                                    ) : (
                                        <Tooltip
                                            content={i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
                                            placement="top"
                                        >
                                            <div
                                                className="ml-auto"
                                                style={{
                                                    cursor: 'not-allowed',
                                                    display: 'flex',
                                                }}
                                            >
                                                <Dropdown.Item
                                                    style={{pointerEvents: 'none'}}
                                                    disabled
                                                    data-cy="transcription-tasks-list-btn">
                                                    <Icon name="mdi-delete-outline" className="mr-2" /> {i18n.t('jt_delete.text')}
                                                </Dropdown.Item>
                                            </div>
                                        </Tooltip>
                                    )
                                }
                            </Dropdown.Menu>
                        </Dropdown>
                    </Tooltip>
                </div>
            ),
        },
    ];

    return (
        <>
            <HelpTextBlock helpText={i18n.t('ht_the_transcription_tasks.text')} />

            <Row>
                <Col lg={3}>
                    <Form
                        className="mb-2 search-box"
                        onSubmit={(e: React.FormEvent) => {
                            e.preventDefault();
                        }}
                    >
                        <Form.Control
                            type="text"
                            placeholder={i18n.t('es_search.text')}
                            onChange={(event) => inputChange(event)}
                        />
                    </Form>
                </Col>
            </Row>

            <LoadingWrapper
                loading={loadingTranscriptionTasks}
                loadingText={i18n.t('jt_loading_data_general.text')}
                minHeight="300px"
            >
                <div className="card">
                    <div className="card-body">
                        <TableComponent data={transcriptionTasks} columns={columns} />
                    </div>
                </div>
                <PaginationComponent
                    pages={pages}
                    currentPage={currentPage}
                    onPageChange={handlePageChange}
                />

                {
                    settingsModalOpen ? (
                        <TranscriptionTasksSettingsModal
                            modalOpen={settingsModalOpen}
                            closeModal={closeSettingsModal}
                            activeTranscriptionTaskId={selectedTranscriptionTaskId}
                        />
                    ) : null
                }
            </LoadingWrapper>
            <ModalAlert
                isOpen={deleteConfirmOpen}
                onClose={closeDeleteConfirm}
                variant="error"
                title={i18n.t('jt_confirm_deletion.text')}
                message={i18n.t('es_are_you_sure_delete_task.text', {externalTaskId: taskToDelete?.externalTaskId})}
                actions={[
                    {
                        label: i18n.t('jt_delete.text'),
                        onClick: confirmDelete,
                        variant: 'danger',
                        loading: isDeleting
                    }
                ]}
                dismissible={!isDeleting}
            />
        </>
    );
});

export default TranscriptionTasksListView; 